import {Alert, Box, Chip, Divider, Link, Stack, Typography} from "@mui/material";
import {useEffect, useState} from "react";
import {useHistory, useParams} from "react-router-dom";
import {DataTable} from "../../../Component/Common/Components/DataTable/DataTable";
import {EntriesPerPage} from "../../../Component/Model/Table/Table";
import {RequestTrackerService} from "../../../Component/Api/Staging/RequestTrackerService";
import {ValidationReview} from "../../../Component/Model/Dto/Staging/ValidationReview";
import {SkeletonLoading} from "../../../Component/Common/SkeletonLoading/SkeletonLoading";
import {StringDateTimeHelper} from "../../../Component/Model/helpers";
import {LoadingButton} from "@mui/lab";
import {PayrollUnattributedFailuresMapper, PayrollValidationReviewMapper} from "./PayrollValidationReviewMapper";
import {SalaryBatchBreakdownTable} from "./SalaryBatchBreakdownTable";
import {StagedImportFileLinks} from "../../Common/ImportAndExport/StagedImportFileLinks";

export const PayrollValidationReview = () => {
    const {requestId} = useParams<{ requestId: string }>();
    const history = useHistory();

    const [review, setReview] = useState<ValidationReview.Dto>();
    const [initialLoading, setInitialLoading] = useState<boolean>(true);
    const [notFound, setNotFound] = useState<boolean>(false);

    // The failing employees page server-side (ordered by name via a non-encrypted ordinal).
    const [employeesLoading, setEmployeesLoading] = useState<boolean>(false);
    const [entriesPerPage, setEntriesPerPage] = useState<EntriesPerPage>({defaultValue: 10});
    const [pageNumber, setPageNumber] = useState<number>(1);
    const pageSize = entriesPerPage.defaultValue ?? 10;

    // File-level (unattributed) failures page independently of the employees.
    const [unattributed, setUnattributed] = useState<ValidationReview.FailurePage>();
    const [unattributedLoading, setUnattributedLoading] = useState<boolean>(false);
    const [unattributedEntriesPerPage, setUnattributedEntriesPerPage] = useState<EntriesPerPage>({defaultValue: 10});
    const [unattributedPage, setUnattributedPage] = useState<number>(1);
    const unattributedPageSize = unattributedEntriesPerPage.defaultValue ?? 10;

    const requestIdNumber = Number(requestId);

    useEffect(() => {
        const loadReview = async () => {
            setEmployeesLoading(true);
            setNotFound(false);
            try {
                const response = await RequestTrackerService.getValidationReview(requestIdNumber, pageNumber, pageSize);
                setReview(response.data);
            } catch (error: any) {
                if (error?.response?.status === 404) {
                    setNotFound(true);
                } else {
                    console.log(error);
                }
            } finally {
                setEmployeesLoading(false);
                setInitialLoading(false);
            }
        };

        loadReview();
    }, [requestIdNumber, pageNumber, pageSize]);

    useEffect(() => {
        const loadUnattributed = async () => {
            setUnattributedLoading(true);
            try {
                const response = await RequestTrackerService.getValidationReviewUnattributed(requestIdNumber, unattributedPage, unattributedPageSize);
                setUnattributed(response.data);
            } catch (error: any) {
                // A 404 (no dry-run) is surfaced by the review load; nothing extra to show here.
                if (error?.response?.status !== 404) {
                    console.log(error);
                }
            } finally {
                setUnattributedLoading(false);
            }
        };

        loadUnattributed();
    }, [requestIdNumber, unattributedPage, unattributedPageSize]);

    const employeeEntriesChanged = (entries: number) => {
        setEntriesPerPage({defaultValue: entries});
        setPageNumber(1);
    };

    const unattributedEntriesChanged = (entries: number) => {
        setUnattributedEntriesPerPage({defaultValue: entries});
        setUnattributedPage(1);
    };

    if (initialLoading) {
        return <SkeletonLoading formLoading FieldAmount={6} width={"100%"}/>;
    }

    return (
        <>
            <LoadingButton onClick={() => history.push("/organisation/payrollData/fetchData")} sx={{mb: 2}}>
                &larr; Back to requests
            </LoadingButton>

            <Typography variant="h4" fontWeight="bold" style={{paddingBottom: "10px"}}>
                Validation Review
            </Typography>

            {notFound && (
                <Alert severity="info">No dry-run validation has been run for this request yet.</Alert>
            )}

            {review && (
                <>
                    <Stack direction="row" spacing={2} alignItems="center" mb={1}>
                        <Chip
                            label={review.passed ? "Validation passed" : "Validation failed"}
                            color={review.passed ? "success" : "error"}
                        />
                        {review.validatedOn && (
                            <Typography variant="body2" color="secondary">
                                Validated {StringDateTimeHelper(review.validatedOn.toString(), true)}
                            </Typography>
                        )}
                    </Stack>

                    <Box mb={2} sx={{display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center"}}>
                        <StagedImportFileLinks
                            requestId={requestIdNumber}
                            hasStagedSalaries={review.salaryBatch != null}/>
                        <Link
                            component="button"
                            type="button"
                            onClick={() => history.push(`/organisation/payrollData/staged/${requestIdNumber}`)}
                            underline="always"
                            color="primary"
                            sx={{cursor: "pointer", typography: "caption"}}>
                            Browse the staged data
                        </Link>
                    </Box>

                    <Typography variant="h6" mt={2}>Sections</Typography>
                    <Box sx={{display: "flex", flexWrap: "wrap", gap: 2, mb: 2}}>
                        {review.sections.map((section, index) => (
                            <Chip
                                key={index}
                                variant="outlined"
                                color={section.passed ? "success" : "error"}
                                label={`${section.name}: ${section.rowsFailed} failed / ${section.rowsCorrect} correct`}
                            />
                        ))}
                    </Box>

                    <Divider/>

                    {review.salaryBatch && (
                        <>
                            <SalaryBatchBreakdownTable salaryBatch={review.salaryBatch}/>
                            <Divider/>
                        </>
                    )}

                    <Typography variant="h6" mt={2} mb={1}>
                        Failures by employee ({review.totalEmployees})
                    </Typography>
                    <DataTable
                        table={PayrollValidationReviewMapper(review.employees)}
                        entriesPerPage={entriesPerPage}
                        pageNumber={pageNumber}
                        totalItemsCount={review.totalEmployees}
                        filteredItemsCount={review.totalEmployees}
                        isLoading={employeesLoading}
                        canSearch={false}
                        entriesPerPageChanged={employeeEntriesChanged}
                        pageNumberChanged={setPageNumber}
                        noDataMessage="No employee-level failures."
                    />

                    {review.totalUnattributed > 0 && unattributed && (
                        <Box mt={3}>
                            <Typography variant="h6" mb={1}>
                                File-level failures ({unattributed.total})
                            </Typography>
                            <Typography variant="body2" color="secondary" mb={1}>
                                Failures that could not be tied to a specific employee.
                            </Typography>
                            <DataTable
                                table={PayrollUnattributedFailuresMapper(unattributed.failures)}
                                entriesPerPage={unattributedEntriesPerPage}
                                pageNumber={unattributedPage}
                                totalItemsCount={unattributed.total}
                                filteredItemsCount={unattributed.total}
                                isLoading={unattributedLoading}
                                canSearch={false}
                                entriesPerPageChanged={unattributedEntriesChanged}
                                pageNumberChanged={setUnattributedPage}
                                noDataMessage="No file-level failures."
                            />
                        </Box>
                    )}
                </>
            )}
        </>
    );
};
